import { Controller } from "stimulus";

const csrfToken = (): string => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

export default class extends Controller {
  static targets = ["candidateInput", "candidateResults", "candidateRow", "sendTo", "historyCard", "mailType", "deliveryMode", "panel", "result", "chain", "step"];
  static values = { candidatesUrl: String, eligibilityUrl: String, sendsUrl: String, bulkUrl: String };

  declare readonly candidateInputTarget: HTMLInputElement;
  declare readonly candidateResultsTarget: HTMLElement;
  declare readonly candidateRowTarget: HTMLElement;
  declare readonly sendToTargets: HTMLButtonElement[];
  declare readonly historyCardTarget: HTMLElement;
  declare readonly hasHistoryCardTarget: boolean;
  declare readonly mailTypeTargets: HTMLButtonElement[];
  declare readonly deliveryModeTargets: HTMLButtonElement[];
  declare readonly panelTarget: HTMLElement;
  declare readonly resultTarget: HTMLElement;
  declare readonly chainTarget: HTMLSelectElement;
  declare readonly stepTarget: HTMLSelectElement;
  declare readonly hasChainTarget: boolean;
  declare readonly hasStepTarget: boolean;
  declare candidatesUrlValue: string;
  declare eligibilityUrlValue: string;
  declare sendsUrlValue: string;
  declare bulkUrlValue: string;

  private botId = "";
  private bpId = "";
  private mailType = "initial";
  private deliveryMode = "mailcatcher";
  private sendTo = "one";
  private timer?: number;
  private requestId = 0;

  pickCampaign(event: Event): void {
    this.botId = (event as CustomEvent<{ id: string }>).detail.id;
    this.bpId = "";
    this.shareSelection();
    this.candidateInputTarget.disabled = false;
    this.candidateInputTarget.placeholder = "Search by name or email";
    this.candidateInputTarget.value = "";
    this.resultTarget.innerHTML = "";
    if (this.sendTo === "campaign") return void this.loadBulk();
    this.searchCandidates();
  }

  afterHistoryCleared(): void {
    if (this.sendTo === "campaign") return void this.loadBulk();
    this.refresh(false);
  }

  private shareSelection(): void {
    if (!this.hasHistoryCardTarget) return;
    this.historyCardTarget.dataset.botId = this.botId;
    this.historyCardTarget.dataset.bpId = this.bpId;
  }

  setSendTo(event: Event): void {
    this.sendTo = this.press(event, this.sendToTargets);
    this.candidateRowTarget.hidden = this.sendTo === "campaign";
    this.resultTarget.innerHTML = "";
    if (this.sendTo === "campaign") return void this.loadBulk();
    this.panelTarget.innerHTML = "";
    this.refresh(false);
  }

  async loadBulk(): Promise<void> {
    if (!this.botId) {
      this.panelTarget.innerHTML = '<p class="muted">Pick a campaign to check its candidates.</p>';
      return;
    }
    const requestId = ++this.requestId;
    const url = new URL(this.bulkUrlValue, window.location.origin);
    Object.entries({ bot_id: this.botId, mail_type: this.mailType, delivery_mode: this.deliveryMode }).forEach(([key, value]) => url.searchParams.set(key, value));
    this.panelTarget.innerHTML = '<p class="muted">Checking the first candidates…</p>';
    try {
      const html = await (await this.get(url, "text/html")).text();
      if (requestId === this.requestId) this.panelTarget.innerHTML = html;
    } catch {
      if (requestId === this.requestId) this.panelTarget.innerHTML = '<p class="muted">Couldn\'t check the campaign. Try again.</p>';
    }
  }

  searchCandidates(): void {
    if (!this.botId) return;
    this.debounce(async () => {
      const url = new URL(this.candidatesUrlValue, window.location.origin);
      url.searchParams.set("bot_id", this.botId);
      url.searchParams.set("q", this.candidateInputTarget.value);
      this.candidateResultsTarget.innerHTML = await (await this.get(url, "text/html")).text();
    });
  }

  pickCandidate(event: Event): void {
    const button = event.currentTarget as HTMLElement;
    this.bpId = button.dataset.id || "";
    this.shareSelection();
    this.candidateInputTarget.value = button.dataset.label || "";
    this.candidateResultsTarget.innerHTML = "";
    this.resultTarget.innerHTML = "";
    this.refresh(false);
  }

  setMailType(event: Event): void {
    this.mailType = this.press(event, this.mailTypeTargets);
    if (this.sendTo === "campaign") return void this.loadBulk();
    this.refresh(false);
  }

  setDeliveryMode(event: Event): void {
    this.deliveryMode = this.press(event, this.deliveryModeTargets);
    if (this.sendTo === "campaign") return void this.loadBulk();
    this.refresh();
  }

  async refresh(keepChain: boolean | Event = true): Promise<void> {
    if (!this.bpId || this.sendTo === "campaign") return;
    const requestId = ++this.requestId;
    const url = new URL(this.eligibilityUrlValue, window.location.origin);
    Object.entries(this.params(keepChain !== false)).forEach(([key, value]) => url.searchParams.set(key, value));
    this.panelTarget.innerHTML = '<p class="muted">Checking every rule…</p>';
    try {
      const html = await (await this.get(url, "text/html")).text();
      if (requestId === this.requestId) this.panelTarget.innerHTML = html;
    } catch {
      if (requestId === this.requestId) this.panelTarget.innerHTML = '<p class="muted">Couldn\'t check eligibility. Try again.</p>';
    }
  }

  async send(event: Event): Promise<void> {
    const button = event.currentTarget as HTMLButtonElement;
    button.disabled = true;
    button.textContent = "Sending…";
    const body = new URLSearchParams(this.params(true));
    try {
      const response = await fetch(this.sendsUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "text/html", "X-Requested-With": "XMLHttpRequest", "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      this.resultTarget.innerHTML = await response.text();
    } catch {
      this.resultTarget.innerHTML = '<div class="tt-result no" role="alert"><strong>Send failed</strong><p>The request didn\'t reach the server. Nothing was sent.</p></div>';
    }
    await this.refresh();
  }

  async switchOff(event: Event): Promise<void> {
    const button = event.currentTarget as HTMLButtonElement;
    const { key = "", url = "", botId = "", deliveryMode = "", scope = "" } = button.dataset;
    button.disabled = true;
    const error = await this.saveRule(url, botId, deliveryMode, false);
    this.showNotice(error ? this.notice(error) : this.notice(`Turned off ${key} for ${scope}.`, () => this.undoSwitch(url, botId, deliveryMode)));
    await this.refresh();
  }

  private async undoSwitch(url: string, botId: string, deliveryMode: string): Promise<void> {
    const error = await this.saveRule(url, botId, deliveryMode, true);
    this.showNotice(error ? this.notice(error) : null);
    await this.refresh();
  }

  private async saveRule(url: string, botId: string, deliveryMode: string, enabled: boolean): Promise<string | null> {
    try {
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "text/html", "X-Requested-With": "XMLHttpRequest", "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ bot_id: botId, delivery_mode: deliveryMode, enabled: String(enabled) }),
      });
      if (response.ok) return null;
      const body = await response.json().catch(() => ({}));
      return body.error || `The rule couldn't be changed (${response.status}).`;
    } catch {
      return "The request didn't reach the server. Nothing was changed.";
    }
  }

  private showNotice(notice: HTMLElement | null): void {
    this.resultTarget.innerHTML = "";
    if (notice) this.resultTarget.appendChild(notice);
  }

  private notice(text: string, undo?: () => void): HTMLElement {
    const row = document.createElement("div");
    row.className = "settings-row is-admin tt-switch-notice";
    row.setAttribute("role", undo ? "status" : "alert");
    const message = document.createElement("div");
    message.className = "settings-row-main";
    message.textContent = text;
    row.appendChild(message);
    if (undo) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "btn btn-sm btn-ghost";
      button.textContent = "Undo";
      button.addEventListener("click", () => { button.disabled = true; undo(); }, { once: true });
      const controls = document.createElement("div");
      controls.className = "settings-controls";
      controls.appendChild(button);
      row.appendChild(controls);
    }
    const card = document.createElement("section");
    card.className = "card settings-card";
    card.appendChild(row);
    return card;
  }

  private params(keepChain: boolean): Record<string, string> {
    const params: Record<string, string> = { bots_prospect_id: this.bpId, mail_type: this.mailType, delivery_mode: this.deliveryMode };
    if (keepChain && this.mailType === "follow_up" && this.hasChainTarget) {
      params.email_chain_id = this.chainTarget.value;
      if (this.hasStepTarget) params.step = this.stepTarget.value;
    }
    return params;
  }

  private press(event: Event, buttons: HTMLButtonElement[]): string {
    const pressed = event.currentTarget as HTMLButtonElement;
    buttons.forEach((button) => button.setAttribute("aria-pressed", String(button === pressed)));
    return pressed.dataset.value || "";
  }

  private get(url: URL, accept: string): Promise<Response> {
    return fetch(url.toString(), { headers: { Accept: accept, "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" }).then((response) => {
      if (!response.ok) throw new Error(String(response.status));
      return response;
    });
  }

  private debounce(run: () => Promise<void>): void {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => run().catch(() => undefined), 250);
  }
}
